import { QIcon_hint_black, QIcon_underline_black } from '@repo/icons/index'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { toast } from 'react-toastify'
import { AccuracyMetricsDetailItem, COMPLEXITY_PARTS_LIST, HIGHLIGHT_METRIC } from '../../../../../model/caf.interface'
import { useCafMetricsDetailsV5 } from '../../../../../modules/apis/webview/caf'
import { app_api_result_postmsg } from '../../../../../modules/consts/caf'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useHtmlParserTransform from '../../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import { mapAccuracyEnum } from '../../../../../modules/hooks/webview/caf/common/useMapAccuracyEnum'
import useRNMsg, { ringleAppNavs } from '../../../../../modules/hooks/webview/common/useRNMsg'
import { s_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import {
  ICON_ALARM_GRAY,
  ICON_CHECK_CIRCLE_GREEN_2,
  ICON_CHEVRON_DOWN_GRAY700,
  ICON_CHEVRON_UP_GRAY700,
} from '../../../../../modules/svg/svg'
import { useConversationAIStack } from '../../../../../pages/[lang]/webview/conversational-ai'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import ErrorReportPopupComponent from '../../../../new-portal/caf/CAFOverview/popup/CafErrorReportPopup'
import Accordion from '../../../../new-portal/caf/common/Accordion'
import AudioController from '../../../../new-portal/caf/common/AudioController'
import ViewMoreButton from '../../../../new-portal/caf/common/ViewMoreButton'
import AccordionContainer from '../../../../new-portal/caf/highlight/AccordionContainer'
import AccordionTitle from '../../../../new-portal/caf/highlight/AccordionTitle'
import WebviewStackContainer from '../../../common/WebviewStackContainer'

const AIAccuracyMetricDetailActivity = ({ params }) => {
  const { t } = useTranslation()
  const { transformHTMLNode } = useHtmlParserTransform()
  const { useConversationAIFlow } = useConversationAIStack()
  const { pop } = useConversationAIFlow()

  const isMobile = useIsMobile()
  const { sendPostMsg, receivePostMsg, removeListener } = useRNMsg()
  const { metricId, error_tag } = params
  const errRef = useRef<HTMLDivElement>(null)
  const {
    data: response,
    error,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useCafMetricsDetailsV5<AccuracyMetricsDetailItem>(+metricId, +error_tag)
  const [data, setData] = useState<AccuracyMetricsDetailItem>(null)
  const [errIndex, setErrIndex] = useState(1)
  const [reportId, setReportId] = useState(0)
  const [sentenceToReport, setSentenceToReport] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const accuracyEnum = mapAccuracyEnum(data?.error_tag, t)
  const toastSuccessMsg = useMemo(() => t(s_caf.accuracy.error_toast), [t])

  useEffect(() => {
    setData({
      ...response?.pages[0],
      details:
        response?.pages?.reduce((ac, cr) => {
          return [...ac, ...cr.details]
        }, []) || [],
    })
  }, [response])

  useEffect(() => {
    const handlePostmsgData = (postMsgData) => {
      if (postMsgData === app_api_result_postmsg.report.success) {
        toast(toastSuccessMsg)
      }
      if (postMsgData === app_api_result_postmsg.report.fail) {
        toast('Try again.')
      }
    }

    receivePostMsg((message) => handlePostmsgData(message))
    return () => {
      removeListener(handlePostmsgData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastSuccessMsg])

  const handleClickErrorReport = (hilightId, content) => () => {
    setReportId(hilightId)
    if (isMobile) {
      sendPostMsg({
        nav: ringleAppNavs.cafErrorReport,
        detail_id: hilightId,
        detail_content: content,
        from: 'metric',
      })
    } else {
      setSentenceToReport(content)
      setShowPopup(true)
    }
  }

  const errorType = useMemo(() => data?.error_type?.toUpperCase(), [data])
  const errorList = data?.details
  const handleIndex = (len) => () => {
    setErrIndex((prev) => (prev > 1 ? 1 : len))
  }

  if (error) return <div className="w-full break-all bg-gray-50 px-[12px]"></div>
  return (
    <div>
      <WebviewStackContainer height="52px" onBack={pop} title={COMPLEXITY_PARTS_LIST[errorType]} border={true}>
        {isLoading || !data ? (
          <div className="flex min-h-[200px] items-center">
            <LoadingSpinner lg primary show />
          </div>
        ) : (
          <div className="keep-all min-h-screen break-words bg-white px-[20px] pt-[16px] pb-[32px] text-gray-700">
            <h2 className="text-sBody mb-[16px]">
              {t(s_caf.accuracy.subsection_info(data?.count || 0, accuracyEnum))}
            </h2>
            <AccordionContainer>
              {errorList?.map((errorItem, idx) => (
                <Accordion
                  key={idx + 'grammar_errorItem'}
                  title={
                    <AccordionTitle type={HIGHLIGHT_METRIC.GRAMMAR} content={errorItem} index={idx + 1} tag={''} />
                  }
                  className="mb-[12px] shadow-sm"
                  errHeight={errRef.current?.style.height}
                  idx={idx}
                >
                  <article className="mb-[8px] text-gray-700">
                    <div className="flex">
                      <img src={ICON_CHECK_CIRCLE_GREEN_2} alt="check" className="h-20 w-20" />
                      <span className="text-medium ml-[10px] text-[14px] font-medium">
                        {ReactHtmlParser(errorItem.correct_sentence, { transform: transformHTMLNode })}
                      </span>
                    </div>
                    <p className="text-sBody my-[16px] rounded bg-gray-50 p-[8px] text-[14px] ">
                      <div ref={errRef}>
                        {errorItem.errors?.slice(0, errIndex).map((item, idx) => {
                          return (
                            <div className="mb-8" key={idx}>
                              <div className="mb-8 flex items-center">
                                <QIcon_hint_black className="mr-[8px]" />
                                <span className="text-h5 mb-[4px] h-12 gap-[4px] font-medium text-black">
                                  {item.title}
                                </span>
                              </div>
                              <span key={idx} className="text-sBody mb-4 block tracking-tight">
                                {item.body}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                      {errorItem.errors?.length > 1 && errIndex > 1 && (
                        <div className="mb-[16px] ml-[8px] flex-row">
                          <QIcon_underline_black width={24} height={24} className="inline" />
                          <span className="text-sm text-gray-600">{t(s_caf.accuracy.underline_text)}</span>
                        </div>
                      )}
                      {errorItem.errors?.length > 1 && (
                        <div className="flex items-center">
                          {errIndex === 1 ? (
                            <>
                              <div className="mr-4" onClick={handleIndex(errorItem.errors?.length)}>
                                {t(s_caf.accuracy.other_errors(errorItem.errors?.length - 1))}
                              </div>
                              <img src={ICON_CHEVRON_DOWN_GRAY700} width={10} height={5} alt="down" className="mb-4" />
                            </>
                          ) : (
                            <>
                              <div className="mr-4" onClick={handleIndex(errorItem.errors?.length)}>
                                {t(s_caf.accuracy.collapse)}
                              </div>
                              <img src={ICON_CHEVRON_UP_GRAY700} width={12} height={5} alt="up" className="mb-4" />
                            </>
                          )}
                        </div>
                      )}
                    </p>
                    {errorItem.audio_url && <AudioController src={errorItem.audio_url} id={idx} />}
                  </article>
                  <div
                    className="flex items-center text-[12px] font-medium tracking-wide text-gray-500 sm:text-[14px]"
                    onClick={handleClickErrorReport(errorItem.id, errorItem?.sentence || errorItem?.my_sentence)}
                  >
                    <img src={ICON_ALARM_GRAY} alt="alarm" className="mb-4" />
                    <div className="ml-2 mt-1">{t(s_caf.highlight.report)}</div>
                  </div>
                </Accordion>
              ))}
            </AccordionContainer>
            <ErrorReportPopupComponent
              content={sentenceToReport}
              type="metric"
              isShowEditPopup={showPopup}
              setShowEditPopup={setShowPopup}
              id={reportId}
            />
            <div className="pt-12">
              {response && !!response?.pages[response?.pages.length - 1]?.page?.next && (
                <div className="flex justify-center pb-[88px]">
                  <ViewMoreButton handleClick={fetchNextPage} isFetchingNextPage={isFetchingNextPage} />
                </div>
              )}
            </div>
          </div>
        )}
      </WebviewStackContainer>
    </div>
  )
}

export default AIAccuracyMetricDetailActivity
